import {Icon, PopoverHandler} from '@forecast-it/design-system';
import moment from 'moment';
import React, {useEffect, useMemo, useReducer, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {TaskParentName} from '../../../../../components/new-ui/task-table-v3/table_row.styled';
import {projectUrl} from '../../../../../directApi';
import {CUSTOM_FIELD_PREFIX} from '../../../../project-tab/projects/scoping-page/ProjectScopingUtil';
import {
	isTimeOffBlockedByBambooHR,
	isTimeOffBlockedByHiBob,
} from '../../../../shared/components/edit-time-entry/EditTimeEntriesLogic';
import EmptyState, {EMPTY_STATE} from '../../../../shared/components/empty-states/empty_state';
import InlineLoader from '../../../../shared/components/inline-loader/inline_loader';
import {convertIntoFloatHoursFormatWithGranularity} from '../../../../shared/components/inputs/hours-input/hours_input_logic';
import {HoursInputContainerStyle} from '../../../../shared/components/inputs/hours-input/hours_input_styled';
import {HoursInput} from '../../../../shared/components/inputs/hours-input/hours_input_view';
import TextAreaFoldout from '../../../../shared/components/inputs/textarea_foldout';
import HarvestPopOut from '../../../../shared/components/popups/harvest_pop_out';
import NotesPopOut from '../../../../shared/components/popups/notes_pop_out';
import Unit4PopOut from '../../../../shared/components/popups/unit4_pop_out';
import {getNavigationString} from '../../../../shared/components/project-indicator/project_group_indicator';
import StarTask from '../../../../shared/components/star/star_task';
import ForecastTooltip from '../../../../shared/components/tooltips/ForecastTooltip';
import {isDateDisabled} from '../../../../shared/util/DateUtil';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {hasTopDownProgramBudgetFeature} from '../../../../shared/util/ProgramFinancialLogic';
import {
	isBillableSplitAllowed,
	isExceedingEstimateAllowed,
	isRolesAllowed,
	isTimeRegistrationNoteRequired,
} from '../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import Util from '../../../../shared/util/util';
import {TIME_PERIOD} from '../../timesheets_change_date_view';
import {workingHourForTheDay} from '../../timesheets_person_data';
import {getTimeAndWorkingHoursForPeriod} from '../../timesheets_time_registrations';
import {timeEntryLockedInternalTime} from '../TimeApprovalLockLogic';
import {buildWeekArr, TIME_REG_TYPES} from '../TimeRowUtils';
import {CustomFieldValueInput} from '../../../../../components/CustomFieldValueInput';
import TimeRegistrationDayTotals from './TimeRegistrationDayTotals';
import {StickyFooterWrapper, TimeRowTableWrapper, VerticallyCentered} from './TimeRowTableWrapper_styled';
import TimesheetsTimeTableTotals, {ALIGN} from './TimesheetsTimeTableTotals';
import AdditionalPropertyDifferenceIndicator from './advanced-time-registration-input/AdditionalPropertyDifferenceIndicator';
import CreateAdvancedTimeRegistrationContent from './advanced-time-registration-input/CreateAdvancedTimeRegistrationContent';
import HoursInputButton from './advanced-time-registration-input/HoursInputButton';
import UpdateAdvancedTimeRegistrationContent from './advanced-time-registration-input/UpdateAdvancedTimeRegistrationContent';
import CheckMarkAndTime from './checkmark-time';
import {EmptyStateTaskName} from './table-content/EmptyStateTaskName';
import {
	editTimeRegistration,
	getActionMenuOptions,
	isContextVisible,
	showDeleteConfirmationModal,
	showProgramRevenueLockedMessage,
} from './timesheet-table-util/ContextMenuUtil';
import {formatDayRegs} from './timesheet-table-util/DataUtil';
import {
	checkIfRoleIsDifferent,
	getInfoTextForHoursInput,
	getInputRegistrationNotAllowedReason,
	getLineItemRegistrationNotAllowedReason,
	getRemainingMinutes,
	getTimeEntryApprovalStatus,
	hasLinkedAllocation,
	isDoneOrHalted,
	lineItemIsTimeOff,
	lineItemWithNotes,
	projectIsManualProgress,
	timeEntryContainsHarvestTask,
	timeEntryContainsMultipleNotes,
	timeEntryContainsNotes,
	timeEntryInLockedPeriod,
	timeEntryInvoiced,
	timeEntryLockedTimeOff,
	timeEntryNoAccess,
	timeEntryProjectDone,
	timeEntryProjectHalted,
} from './timesheet-table-util/InformationUtil';
import {
	fetchHarvestTasks,
	fetchUnit4Activities,
	getIsHarvestLineItem,
	getIsUnit4LineItem,
	shouldDisableHarvestPopup,
	shouldDisableUnit4Popup,
} from './timesheet-table-util/IntegrationUtil';
import {
	createTimeReg,
	deleteTimeRegistration,
	deleteTimeRegistrationsForWeek,
	handleWeekDayHoursInputChange,
	handleDayHoursInputChange,
	handleNotesFieldBlur,
	onTimeCreateSuccess,
	onTimeDeleteSuccess,
	onTimeUpdateSuccess,
} from './timesheet-table-util/MutationUtil';
// Have to use non-fragment version because Timesheet is a mix of JS and TS, and fragments aren't compatible across filetype yet.
import {DeprecatedProjectIndicatorNoFragment} from '../../../../shared/components/project-indicator/DeprecatedProjectIndicator';
import TimeregActionButton from '../../../../shared/components/time-reg-table/TimeregActionButton';
import {EntityType} from '../../../../admin-tab/custom-fields-page/Constants';

export const TimesheetsTimeRowTable = ({
	entityList,
	sort,
	setExternalMovedTasks = () => void 0,
	setExternalMovedProjects = () => void 0,
	setExternalMovedIdleTimes = () => void 0,
	loading,
	viewer,
	currentViewingDate,
	timeRegistrations,
	timePeriod,
	columns,
	selectedPerson,
	filterActive,
	searchActive,
	usingCurrentUserAssigneeFilter,
	selectDate,
	holidays,
	lockedDate,
	isFullyLocked,
	usingTimeApproval,
	usingInternalTimeApproval,
	periodTimeApprovalStatus,
	overBudgetProgramsByCompanyProjectId,
	timeRegistrationTargetSuggestionsData,
	goToTimesheetsTab,
	thisWeekWork,
	showSuggestions,
	hasSuggestions,
	refetchEntries,
}) => {
	const intl = useIntl();
	const history = useHistory();

	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const usingAdvancedTimeRegistration = isTimeRegistrationNoteRequired() || isBillableSplitAllowed() || isRolesAllowed();

	const usingSageIntacctForTimesheets =
		hasFeatureFlag('sage_intacct_integration') && hasFeatureFlag('use_sage_service_for_time_regs');

	const [focusedInput, setFocusedInput] = useState();

	const [timeRegToFocus, setTimeRegToFocus] = useState();
	const [movedTasks, setMovedTasks] = useState([]);
	const [movedProjects, setMovedProjects] = useState([]);
	const [movedIdleTimes, setMovedIdleTimes] = useState([]);
	const [harvestTasks, setHarvestTasks] = useState([]);
	const [harvestLoading, setHarvestLoading] = useState([]);
	const [harvestProjectLoaded, setHarvestProjectLoaded] = useState(null);
	const [unit4Activities, setUnit4Activities] = useState([]);
	const [unit4Loading, setUnit4Loading] = useState([]);
	const [unit4ProjectLoaded, setUnit4ProjectLoaded] = useState(null);
	const [operationInProgress, setOperationInProgress] = useState(false);

	const weekStart = useMemo(() => currentViewingDate.clone().startOf('week'), [currentViewingDate]);
	const weekEnd = useMemo(() => currentViewingDate.clone().endOf('week'), [currentViewingDate]);
	const totalsPerDay = getTimeAndWorkingHoursForPeriod(weekStart, weekEnd, timeRegistrations, selectedPerson);

	// Hook hack to allow forceUpdate
	const [forceUpdate, setForceUpdate] = useReducer(x => x + 1, 0);

	const isWeekendLocked = isDateDisabled(currentViewingDate);

	const useTimeOffApproval = viewer.company.useTimeOffApproval;

	const hourInputs = [];

	useEffect(() => {
		focusedInput != null && hourInputs[focusedInput] ? hourInputs[focusedInput].focus() : void 0;
	}, [focusedInput]);

	const addMovedTask = taskId => {
		const curMovedTasks = [...movedTasks];
		curMovedTasks.push(taskId);
		setMovedTasks(curMovedTasks);
		setExternalMovedTasks(curMovedTasks);
	};

	const addMovedProject = projectId => {
		const curMovedProjects = [...movedProjects];
		curMovedProjects.push(projectId);
		setMovedProjects(curMovedProjects);
		setExternalMovedProjects(curMovedProjects);
	};

	const addMovedIdleTime = idleTimeId => {
		const curMovedIdleTimes = [...movedIdleTimes];
		curMovedIdleTimes.push(idleTimeId);
		setMovedIdleTimes(curMovedIdleTimes);
		setExternalMovedIdleTimes(curMovedIdleTimes);
	};

	useEffect(() => {
		// Clear when changing selected switches, currentViewingDate, or timePeriod
		setMovedTasks([]);
		setMovedProjects([]);
		setExternalMovedTasks([]);
		setMovedIdleTimes([]);
		setExternalMovedIdleTimes([]);
	}, [currentViewingDate, timePeriod, isFullyLocked, sort.value]);

	const onInputKeyDown = (e, idx) => {
		// Currently only works for week view, had some funkay behavior on day view with switch tasks enabled
		if (isWeekView && e && e.key) {
			let diff = 0;
			const key = e.key;
			switch (key) {
				case 'ArrowRight':
					diff = 1;
					break;
				case 'ArrowLeft':
					diff = -1;
					break;
				case 'ArrowDown':
					diff = 7;
					break;
				case 'ArrowUp':
					diff = -7;
					break;
				default:
			}
			if (diff !== 0) {
				const newIdx = Util.clamp(idx + diff, 0, hourInputs.length - 1);
				setFocusedInput(newIdx);
			}
		}
	};

	const {formatMessage, formatNumber} = intl;

	// region Extracted utility functions
	const _fetchHarvestTasks = project => {
		fetchHarvestTasks(
			project,
			harvestLoading,
			harvestTasks,
			harvestProjectLoaded,
			setHarvestLoading,
			setHarvestProjectLoaded,
			setHarvestTasks
		);
	};

	const _fetchUnit4Activities = project => {
		fetchUnit4Activities(
			project,
			unit4Loading,
			unit4Activities,
			unit4ProjectLoaded,
			setUnit4Loading,
			setUnit4ProjectLoaded,
			setUnit4Activities
		);
	};

	const _onTimeCreateSuccess = (result, companyProjectId) => {
		const program = overBudgetProgramsByCompanyProjectId.find(
			project => project.companyProjectId === companyProjectId
		)?.programInfo;
		setOperationInProgress(false);
		onTimeCreateSuccess(result, intl, program, viewer.actualPersonId);
		refetchEntries();
	};

	const _onTimeUpdateSuccess = result => {
		setOperationInProgress(false);
		onTimeUpdateSuccess(result, intl, overBudgetProgramsByCompanyProjectId, viewer.actualPersonId);
	};

	const _onTimeDeleteSuccess = result => {
		setOperationInProgress(false);
		onTimeDeleteSuccess(result, intl);
	};

	const _handleNotesFieldBlur = (lineItem, value, date, isTimeReg) => {
		handleNotesFieldBlur.call(
			this,
			lineItem,
			value,
			isTimeReg,
			viewer,
			date,
			selectedPerson,
			_onTimeCreateSuccess,
			getIsHarvestLineItem,
			setForceUpdate,
			getIsUnit4LineItem,
			addMovedIdleTime,
			addMovedProject,
			addMovedTask,
			_onTimeUpdateSuccess
		);
	};

	const _createTimeReg = (
		date,
		hours,
		billableHours,
		roleId,
		notes,
		taskId,
		parentTaskId,
		projectId,
		companyProjectId,
		idleTimeId,
		selectedPerson,
		harvestTask,
		unit4Activity,
		onSuccess
	) => {
		createTimeReg(
			viewer,
			taskId,
			idleTimeId,
			projectId,
			companyProjectId,
			parentTaskId,
			hours,
			billableHours,
			roleId,
			notes,
			date,
			selectedPerson,
			harvestTask,
			unit4Activity,
			_onTimeCreateSuccess,
			timeRegistrationTargetSuggestionsData,
			onSuccess
		);
	};

	const _handleWeekDayHoursInputChange = (
		timeRegId,
		hours,
		billableHours,
		roleId,
		notes,
		lineItem,
		inputDate,
		timeForDay,
		selectedPerson,
		onSuccess
	) => {
		setOperationInProgress(true);
		handleWeekDayHoursInputChange.call(
			this,
			hours,
			selectedPerson,
			viewer,
			inputDate,
			timeForDay,
			lineItem,
			setTimeRegToFocus,
			addMovedIdleTime,
			addMovedProject,
			addMovedTask,
			getIsHarvestLineItem,
			_createTimeReg,
			billableHours,
			roleId,
			notes,
			setForceUpdate,
			getIsUnit4LineItem,
			usingAdvancedTimeRegistration,
			timeRegId,
			_onTimeUpdateSuccess,
			setOperationInProgress,
			onSuccess
		);
	};

	const _handleDayHoursInputChange = (hours, billableHours, roleId, notes, lineItem, selectedPerson, onSuccess) => {
		setOperationInProgress(true);
		handleDayHoursInputChange.call(
			this,
			hours,
			selectedPerson,
			viewer,
			currentViewingDate,
			lineItem,
			addMovedIdleTime,
			addMovedProject,
			addMovedTask,
			getIsHarvestLineItem,
			_createTimeReg,
			billableHours,
			roleId,
			notes,
			setForceUpdate,
			getIsUnit4LineItem,
			_onTimeDeleteSuccess,
			onSuccess
		);
	};

	const _deleteTimeRegistration = lineItem => {
		deleteTimeRegistration(lineItem, viewer, _onTimeDeleteSuccess);
	};

	const _deleteTimeRegistrationsForWeek = dayRegsForWeek => {
		deleteTimeRegistrationsForWeek(dayRegsForWeek, _onTimeDeleteSuccess);
	};

	const _showProgramRevenueLockedMessage = (program, companyProjectId) => {
		showProgramRevenueLockedMessage(viewer, program, companyProjectId);
	};

	const _editTimeRegistration = lineItem => {
		editTimeRegistration(lineItem, overBudgetProgramsByCompanyProjectId, _showProgramRevenueLockedMessage, selectedPerson);
	};

	const _showDeleteConfirmationModal = (lineItem, isWeek) => {
		showDeleteConfirmationModal.call(
			this,
			formatMessage,
			isWeek,
			_deleteTimeRegistration,
			lineItem,
			_deleteTimeRegistrationsForWeek
		);
	};

	const _isDoneOrHalted = project => {
		return isDoneOrHalted(project);
	};

	const _shouldDisableHarvestPopup = (lineItem, isWeekView) => {
		return shouldDisableHarvestPopup(isWeekView, lineItem, _isDoneOrHalted);
	};

	const _shouldDisableUnit4Popup = (lineItem, isWeekView) => {
		return shouldDisableUnit4Popup(isWeekView, lineItem, _isDoneOrHalted);
	};
	// endregion

	// region Render
	const gridStyle = {
		// Build grid depending on enabled columns
		'--gridCols': columns.reduce((acc, col) => {
			switch (col.id) {
				case 'switch-task-indicator':
					return acc + ' 62px';
				case 'project-indicator':
					return acc + ' 55px';
				case 'task-name':
				case 'internal-time-name':
					return acc + ' 1fr';
				case 'project-name':
				case 'phase':
				case 'client':
				case 'remaining':
				case 'harvest':
				case 'unit4':
				case 'notes-input':
				case 'notes-button':
					return acc + ' auto';
				case 'hour-inputs-day':
				case 'hour-inputs-week':
					return acc + ' min-content';
				case 'star':
				case 'edit':
				case 'delete':
					return acc + ' 30px';
				default:
					if (col.id.startsWith(CUSTOM_FIELD_PREFIX)) {
						return acc + ' 120px';
					}
					return acc;
			}
		}, ''),
	};

	const getHeaderElemForCol = col => {
		const sortClass = 'sortable ' + (sort.value === col.id ? (sort.ascending ? 'ascending' : 'descending') : '');
		const stickyClass = ' sticky ';

		const toggleColumnSort = () => {
			if (sort.value === col.id) {
				sort.setAscending(!sort.ascending);
			} else {
				sort.setValue(col.id);
				sort.setAscending(true);
			}
		};

		switch (col.id) {
			case 'project-indicator':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
					>
						<div title={formatMessage({id: 'common.project_id_short'})}>
							{formatMessage({id: 'common.project_id_short'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'project-name':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
						tabIndex="0"
					>
						<div className={'header-text'} title={formatMessage({id: 'common.project'})}>
							{formatMessage({id: 'common.project'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'task-name':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
						tabIndex="0"
					>
						<div className={'header-text'} title={formatMessage({id: 'common.task'})}>
							{formatMessage({id: 'common.task'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'internal-time-name':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
						tabIndex="0"
					>
						<div className={'header-text'} title={formatMessage({id: 'common.internal_time'})}>
							{formatMessage({id: 'common.internal_time'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'phase':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
					>
						<div className={'header-text'} title={formatMessage({id: 'common.phase'})}>
							{formatMessage({id: 'common.phase'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'client':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
					>
						<div className={'header-text'} title={formatMessage({id: 'common.client'})}>
							{formatMessage({id: 'common.client'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'remaining':
				// No longer possible to sort by remaining
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper ' + stickyClass + col.id}>
						<div className={'header-text'} title={formatMessage({id: 'common.remaining'})}>
							{formatMessage({id: 'common.remaining'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			//TODO: this should eventually be split into one column per week day
			case 'hour-inputs-week': {
				const weekDays = buildWeekArr(currentViewingDate.clone().endOf('week'));
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper ' + stickyClass + 'hour-inputs'}>
						{weekDays.map((day, idx) => {
							const nonWorkingDay = holidays[idx].length > 0 || workingHourForTheDay(selectedPerson, day) === 0;
							const dayTotals = totalsPerDay[idx];
							if (dayTotals.hideDay) {
								return null;
							}
							return (
								<div
									key={'header-' + col.id + day.format('ll')}
									className={
										'week-day-header' +
										(moment(day).isSame(moment(), 'day') ? ' today' : '') +
										(nonWorkingDay ? ' non-working-day' : '')
									}
									onClick={() => selectDate(day)}
								>
									<div className={'weekday'}>{day.format('dddd')}</div>
									<div className={'date-text'}>{day.format('DD MMM')}</div>
								</div>
							);
						})}
					</div>
				);
			}
			case 'hour-inputs-day':
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper ' + stickyClass + 'hour-inputs'}>
						<div className={'time-header'}>{formatMessage({id: 'common.time_entry'})}</div>
					</div>
				);
			case 'notes-button':
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper notes-button-wrapper' + stickyClass}>
						<div className={'header-text'} title={formatMessage({id: 'common.notes'})}>
							{formatMessage({id: 'common.notes'})}
						</div>
					</div>
				);
			case 'notes-input':
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper notes-input-wrapper' + stickyClass}>
						<div className={'header-text'} title={formatMessage({id: 'common.notes'})}>
							{formatMessage({id: 'common.notes'})}
						</div>
					</div>
				);
			case 'harvest':
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper ' + stickyClass + col.id}>
						<div className={'header-text'} title={formatMessage({id: 'title.app_harvest'})}>
							{formatMessage({id: 'title.app_harvest'})}
						</div>
					</div>
				);
			case 'unit4':
				return (
					<div key={'header-' + col.id} className={'header-elem-wrapper ' + stickyClass + col.id}>
						<div className={'header-text'} title="Unit4">
							Unit4
						</div>
					</div>
				);
			case 'star':
				return (
					<div
						key={'header-' + col.id}
						className={'header-elem-wrapper ' + stickyClass + col.id + ' ' + sortClass}
						onClick={toggleColumnSort}
					>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'edit':
			case 'delete':
			case 'switch-task-indicator':
				return <div key={'header-' + col.id} className={'header-elem-wrapper ' + stickyClass + col.id} />;
			default:
				if (col.id.startsWith(CUSTOM_FIELD_PREFIX)) {
					return (
						<div key={'header-' + col.id} className={'header-elem-wrapper' + stickyClass}>
							{col.customFieldDefinition.displayName}
						</div>
					);
				}
		}
	};

	/**
	 * Returns whether context (action) menu should be visible or not
	 * @param {*} lineItem the item containing all time registrations
	 * @param {*} daysRegisteredForEntity
	 * @param {boolean} isWeekView
	 * @returns {boolean}
	 */
	const _isContextVisible = (lineItem, daysRegisteredForEntity, isWeekView) => {
		return isContextVisible(lineItem, isWeekView, daysRegisteredForEntity, usingInternalTimeApproval, useTimeOffApproval);
	};

	/**
	 * Returns an array of options for the Action Context Menu
	 * @param {object} lineItem
	 * @param {boolean} isWeekView
	 * @param {Array} dayRegsForEntity
	 * @returns {Array}
	 */
	const _getActionMenuOptions = (lineItem, isWeekView, dayRegsForEntity, sageIntacctEnabled) => {
		return getActionMenuOptions.call(
			this,
			isWeekView,
			formatMessage,
			_showDeleteConfirmationModal,
			dayRegsForEntity,
			lineItem,
			_editTimeRegistration,
			sageIntacctEnabled
		);
	};

	const getRowElemForCol = (col, lineItem, rowNr, lastRow) => {
		const indexIndicator = `time-reg-${rowNr}`;
		// The note fiesta is simply too much so checking here and only passing down lineItem without nodes.
		const lineItemNode = lineItem.node ? lineItem.node : lineItem;
		const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;
		const isTimeReg = lineItemNode.type != null;
		const isSuggestion = lineItemNode.showAsSuggestion;
		let task;
		let project;
		let idleTime;
		let nothing;

		if (isTimeReg) {
			if (isWeekView && lineItem.dayRegs != null) {
				// We aren't given time regs, we are given entities under time regs
				if (lineItemNode.type === TIME_REG_TYPES.NO_ACCESS) {
					nothing = true;
				}
				if (lineItemNode.type === TIME_REG_TYPES.TASK_TIME) {
					task = lineItemNode;
					project = task.project;
				} else if (lineItemNode.type === TIME_REG_TYPES.PROJECT_TIME) {
					project = lineItemNode;
					task = null;
				} else if (lineItemNode.type === TIME_REG_TYPES.IDLE_TIME) {
					idleTime = lineItemNode;
				}
			} else {
				// We aren given time regs (day view)
				nothing = lineItemNode.type === TIME_REG_TYPES.NO_ACCESS;
				task = lineItemNode.type === TIME_REG_TYPES.TASK_TIME ? lineItemNode.task : null;
				project = task ? task.project : lineItemNode.type === TIME_REG_TYPES.PROJECT_TIME ? lineItemNode.project : null;
				idleTime = lineItemNode.type === TIME_REG_TYPES.IDLE_TIME ? lineItemNode.idleTime : null;
			}
		} else {
			// For non-timeregs, our entity is either an internal time, a project, or a task
			if (lineItemNode.switchIdleTime) {
				idleTime = lineItemNode;
			} else if (lineItemNode.switchProject) {
				project = lineItemNode.project;
			} else {
				task = lineItemNode.task ? lineItemNode.task : lineItemNode;
				project = task.project;
			}
		}

		const firstElem = columns[0];
		const lastElem = columns[columns.length - 1];
		const isFavorited = task ? task.favoured : idleTime ? idleTime.favoured : false;
		const shouldFocus = task && timeRegToFocus === task.id;

		// Checks for disabled rows
		const projectDone = isTimeReg && timeEntryProjectDone(lineItemNode);
		const projectHalted = isTimeReg && timeEntryProjectHalted(lineItemNode);
		const manualProgress = isTimeReg && projectIsManualProgress(lineItemNode);

		const noAccess = isTimeReg && timeEntryNoAccess(lineItemNode);
		const lockedInternalTime =
			isTimeReg && usingInternalTimeApproval && timeEntryLockedInternalTime(lineItemNode, null, false);
		const lockedTimeOff = isTimeReg && timeEntryLockedTimeOff(lineItemNode, null, null, useTimeOffApproval);
		let inLockedPeriod = isTimeReg && timeEntryInLockedPeriod(lineItemNode, [], isWeekView);
		const invoiced = isTimeReg && timeEntryInvoiced(lineItemNode, [], isWeekView);
		const isInternalTimeDisabled = isTimeReg && lineItemNode.idleTime && lineItemNode.idleTime.disabled;
		const hasAllocation = lineItemNode.allocationId && !(lineItemNode.idleTime && lineItemNode.idleTime.isInternalTime);

		const fullAccessToProject = project && project.fullAccessToProject;

		const noteRequired = isTimeRegistrationNoteRequired();
		const billableSplitAllowed = isBillableSplitAllowed();

		const timeOffLockedByBambooHR = isTimeOffBlockedByBambooHR(idleTime, viewer.company.bambooHREnabled);
		const timeOffLockedByHiBob = isTimeOffBlockedByHiBob(idleTime, viewer.company.hiBobEnabled);

		let additionalClasses = (isFavorited ? ' favorite' : '') + (shouldFocus ? ' focused' : '');
		if (col === firstElem) {
			additionalClasses += ' first';
		} else if (col === lastElem) {
			// must be the star column or the context menu
			additionalClasses += ' last';
		}
		if (lastRow) {
			additionalClasses += ' lastRow';
		}
		let dayLockedInTime =
			!isWeekView && ((lockedDate && lockedDate.isSameOrAfter(currentViewingDate, 'day')) || isWeekendLocked);
		switch (col.id) {
			case 'project-indicator':
				return (
					<div
						id={`project-indicator-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper project-indicator' + additionalClasses}
					>
						{project && (
							<DeprecatedProjectIndicatorNoFragment
								project={project}
								clearSubPath={true}
								disableLink={!fullAccessToProject}
								noTitle={!fullAccessToProject}
							/>
						)}
					</div>
				);
			case 'project-name':
				const idleTimeName =
					idleTime && idleTime.isInternalTime
						? intl.formatMessage({id: 'common.internal_time'})
						: idleTime && !idleTime.isInternalTime
						? intl.formatMessage({id: 'common.time_off'})
						: '';
				const projectName = project
					? fullAccessToProject
						? project.name
						: intl.formatMessage({id: 'timesheets.no_access'})
					: lineItemNode.type === TIME_REG_TYPES.NO_ACCESS
					? intl.formatMessage({id: 'timesheets.no_access'})
					: idleTimeName;
				return (
					<a
						id={`project-name-${indexIndicator}`}
						href={fullAccessToProject ? getNavigationString(project, true) : null}
						title={fullAccessToProject ? projectName : null}
						key={lineItemNode.id + col.id}
						className={
							'elem-wrapper ' +
							col.id +
							' ' +
							additionalClasses +
							(fullAccessToProject ? ' clickable' : '') +
							(nothing || !fullAccessToProject ? ' no-access' : '')
						}
						tabIndex="0"
					>
						{projectName}
					</a>
				);
			case 'task-name':
			case 'internal-time-name':
				const taskName = task
					? task.name
					: lineItemNode.type === TIME_REG_TYPES.IDLE_TIME
					? isWeekView
						? idleTime.name
						: lineItemNode.idleTime.name
					: lineItemNode.switchIdleTime
					? lineItemNode.name
					: '';
				return (
					<div
						data-cy={'time-reg-table-task-name-cell'}
						id={`task-name-${indexIndicator}`}
						title={task ? 'T' + task.companyTaskId + ' - ' + task.name : ''}
						key={lineItemNode.id + col.id}
						className={
							'elem-wrapper task-name ' +
							additionalClasses +
							(task && task.fullAccessToProject ? ' clickable' : '')
						}
						style={{
							paddingTop: 0,
						}}
						tabIndex="0"
						onClick={
							task && task.fullAccessToProject ? () => Util.showTaskModal(task.companyTaskId, history) : null
						}
					>
						<VerticallyCentered>
							{task?.parentTask ? (
								<div>
									<TaskParentName>{task?.parentTask?.name}</TaskParentName>
									{taskName}
								</div>
							) : (
								<div>{taskName}</div>
							)}
						</VerticallyCentered>
					</div>
				);
			case 'phase':
				const phaseLocation =
					task && task.phase && project
						? projectUrl(project.companyProjectId, project.customProjectId) + '/scoping#' + task.phase.id
						: task && task.phase && task.project
						? projectUrl(task.project.companyProjectId, task.project.customProjectId) + '/scoping#' + task.phase.id
						: null;
				return (
					<a
						id={`phase-${indexIndicator}`}
						title={task && task.phase && task.phase.name}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper task-phase ' + additionalClasses + (task && task.phase ? ' clickable' : '')}
						href={phaseLocation}
						disabled={!phaseLocation}
					>
						{task && task.phase && task.phase.name}
					</a>
				);
			case 'client':
				return (
					<div
						id={`client-${indexIndicator}`}
						title={project && project.client && project.client.name}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper project-client-tile ' + additionalClasses}
					>
						{project && project.client && project.client.name}
					</div>
				);
			case 'remaining':
				return (
					<div
						id={`remaining-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper task-remaining-tile ' + additionalClasses}
					>
						{task && project && project.fullAccessToProject
							? task.timeLeft != null
								? project.estimationUnit === 'POINTS'
									? formatNumber((task.timeLeft * 10) / 10, {format: 'rounded_two_decimal'}) +
									  ' ' +
									  formatMessage({id: 'common.points.short'})
									: Util.convertMinutesToFullHour(task.timeLeft, intl, true)
								: null
							: null}
					</div>
				);
			case 'hour-inputs-week': {
				let notPermittedHourInputs = false;

				if (hasTopDownProgramBudgetFeature() && !isBillableSplitAllowed()) {
					const lineItemProject = lineItemNode.task ? lineItemNode.task.project : lineItemNode.project;
					notPermittedHourInputs =
						overBudgetProgramsByCompanyProjectId.find(
							project => project.companyProjectId === lineItemProject?.companyProjectId
						) &&
						(!task || task?.billable === true);
				}

				const lineItemRegistrationNotAllowedReason = getLineItemRegistrationNotAllowedReason(
					lineItemNode,
					selectedPerson.id
				);
				// Only show value for timeRegs or non-empty moved tasks

				const dayRegList = lineItem.dayRegs ? lineItem.dayRegs : [[], [], [], [], [], [], []];

				const showValueForWeekView = dayRegs => isTimeReg && dayRegList.some(el => el.length > 0) && dayRegs.length > 0;
				return (
					<div
						id={`hour-inputs-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={
							'elem-wrapper week-view hours-input-wrapper' +
							(noteRequired ? ' overflow-visible' : '') +
							additionalClasses
						}
					>
						{dayRegList.map((dayRegs, idx) => {
							const dayOfInput = weekStart.clone().add(idx, 'd');
							const inLockedPeriodWeek = isTimeReg && timeEntryInLockedPeriod(lineItemNode, dayRegs, isWeekView);
							const inputRegistrationNotAllowedReason = getInputRegistrationNotAllowedReason(
								dayOfInput,
								lineItemNode
							);
							const approvalStatus =
								usingTimeApproval && getTimeEntryApprovalStatus(lineItemNode, dayRegs, isWeekView);
							const invoicedWeek = isTimeReg && timeEntryInvoiced(lineItemNode, dayRegs, isWeekView);
							let hasAllocationWeek =
								lineItemNode.timeReg &&
								lineItemNode.timeReg.node.allocationId &&
								!(lineItemNode.timeReg.node.idleTime && lineItemNode.timeReg.node.idleTime.isInternalTime);
							if (lineItemIsTimeOff(lineItemNode)) {
								hasAllocationWeek = hasLinkedAllocation(dayRegs);
							}
							const containsNotes = isTimeReg && timeEntryContainsMultipleNotes(dayRegs);
							const hasMultipleHarvestTasks = isTimeReg && timeEntryContainsHarvestTask(dayRegs);
							const nonWorking =
								holidays[idx].length > 0 || workingHourForTheDay(selectedPerson, dayOfInput) === 0;
							const isInternalTimeDisabled =
								isTimeReg &&
								lineItemNode.timeReg &&
								lineItemNode.timeReg.node.idleTime &&
								lineItemNode.timeReg.node.idleTime.disabled;
							const lockedInTime =
								(lockedDate && lockedDate.isSameOrAfter(dayOfInput, 'day')) || isDateDisabled(dayOfInput);
							const lockedInternalTime =
								isTimeReg &&
								usingInternalTimeApproval &&
								timeEntryLockedInternalTime(lineItem, dayRegs, isWeekView);
							const lockedTimeOff =
								isTimeReg && timeEntryLockedTimeOff(lineItem, dayRegs, isWeekView, useTimeOffApproval);
							const infoText = getInfoTextForHoursInput(
								useTimeOffApproval,
								noAccess,
								lockedInternalTime,
								lockedTimeOff,
								inLockedPeriodWeek,
								approvalStatus,
								invoicedWeek,
								projectDone,
								projectHalted,
								containsNotes,
								hasMultipleHarvestTasks,
								hasAllocationWeek,
								isWeekView,
								isInternalTimeDisabled,
								lockedInTime,
								periodTimeApprovalStatus,
								lineItemRegistrationNotAllowedReason,
								inputRegistrationNotAllowedReason,
								intl,
								notPermittedHourInputs,
								timeOffLockedByBambooHR,
								timeOffLockedByHiBob,
								manualProgress
							);
							const isDisabled =
								lineItemRegistrationNotAllowedReason ||
								inputRegistrationNotAllowedReason ||
								inLockedPeriodWeek ||
								invoicedWeek ||
								lockedInTime ||
								nothing ||
								noAccess ||
								lockedInternalTime ||
								lockedTimeOff ||
								hasAllocationWeek ||
								projectHalted ||
								projectDone ||
								(containsNotes && !usingAdvancedTimeRegistration) ||
								hasMultipleHarvestTasks ||
								isInternalTimeDisabled ||
								notPermittedHourInputs ||
								timeOffLockedByBambooHR ||
								timeOffLockedByHiBob ||
								manualProgress ||
								(usingSageIntacctForTimesheets && operationInProgress);
							const key = currentViewingDate.format('x') + lineItemNode.id + col.id + '-day-' + idx;
							const isBillable = lineItemNode.billable;
							const timeRegistered = dayRegs.reduce((acc, day) => day.node.minutesRegistered + acc, 0);
							const billableTimeRegistered = dayRegs.reduce(
								(acc, day) => day.node.billableMinutesRegistered + acc,
								0
							);
							const hasTimeRegistered =
								!!timeRegistered || !!billableTimeRegistered || timeEntryContainsNotes(dayRegs);
							const isBillableDifference =
								billableSplitAllowed && hasTimeRegistered && timeRegistered !== billableTimeRegistered;
							const taskEstimateForecastMinutes = lineItemNode.estimateForecastMinutes;
							const taskTotalMinutesRegistered = lineItemNode.totalMinutesRegistered;
							const remainingMinutes = taskEstimateForecastMinutes - taskTotalMinutesRegistered;
							const validateRemaining = !isExceedingEstimateAllowed() && remainingMinutes !== undefined;
							const canHaveRole = lineItemNode.type !== TIME_REG_TYPES.IDLE_TIME;
							const isRoleDifferent = dayRegs
								? dayRegs.some(dayReg => checkIfRoleIsDifferent(dayReg.node))
								: false;

							const hasDiff = (isBillable && isBillableDifference) || isRoleDifferent;

							const dayTotals = totalsPerDay[idx];
							if (dayTotals.hideDay) {
								return null;
							}
							return usingAdvancedTimeRegistration || hasDiff ? (
								<ForecastTooltip key={key} content={infoText} maxWidth={250}>
									<HoursInputContainerStyle>
										<PopoverHandler>
											<PopoverHandler.Trigger asChild={true}>
												<HoursInputButton
													disabled={isDisabled}
													title={hasDiff ? 'Time entry with additional properties' : null}
													nonWorkingDay={nonWorking}
													minutesRegistered={dayRegs.reduce(
														(acc, day) => day.node.minutesRegistered + acc,
														null
													)}
												>
													{hasDiff && <AdditionalPropertyDifferenceIndicator />}
												</HoursInputButton>
											</PopoverHandler.Trigger>
											<PopoverHandler.Content showArrow={true}>
												{hasTimeRegistered ? (
													<UpdateAdvancedTimeRegistrationContent
														timeRegistrationsOnEntityOnDay={formatDayRegs(
															dayRegs.map(({node}) => node)
														)}
														billable={isBillable}
														mutation={(hours, billableHours, notes, timeRegId, roleId, onSuccess) =>
															_handleWeekDayHoursInputChange(
																timeRegId,
																hours,
																billableHours,
																roleId,
																notes,
																lineItemNode,
																dayOfInput,
																dayRegs,
																selectedPerson,
																onSuccess
															)
														}
														selectedPerson={selectedPerson}
														dayOfInput={dayOfInput}
														canHaveRole={canHaveRole}
														projectId={project?.id}
													/>
												) : (
													<CreateAdvancedTimeRegistrationContent
														billable={isBillable}
														mutation={(hours, billableHours, notes, roleId, onSuccess) =>
															_handleWeekDayHoursInputChange(
																undefined,
																hours,
																billableHours,
																roleId,
																notes,
																lineItemNode,
																dayOfInput,
																dayRegs,
																selectedPerson,
																onSuccess
															)
														}
														selectedPerson={selectedPerson}
														dayOfInput={dayOfInput}
														taskEstimateForecastMinutes={taskEstimateForecastMinutes}
														taskTotalMinutesRegistered={taskTotalMinutesRegistered}
														canHaveRole={canHaveRole}
														projectId={project?.id}
														isSuggestion={isSuggestion}
														taskId={lineItemNode.id}
													/>
												)}
											</PopoverHandler.Content>
										</PopoverHandler>
									</HoursInputContainerStyle>
								</ForecastTooltip>
							) : (
								<HoursInput
									key={key}
									infoText={infoText}
									cy={'time-entry-hours-input'}
									index={idx}
									disabled={isDisabled}
									nonWorkingDay={nonWorking}
									forceUpdate={forceUpdate}
									parentKeyDown={e => onInputKeyDown(e, rowNr * 7 + idx)}
									innerRef={div => (hourInputs[rowNr * 7 + idx] = div)}
									value={
										showValueForWeekView(dayRegs)
											? dayRegs.reduce((acc, day) => day.node.minutesRegistered + acc, 0) / 60
											: null
									}
									mutationValidChange={true}
									maxValueInMinutes={validateRemaining ? remainingMinutes + timeRegistered : undefined}
									enableSpinner={true}
									mutation={(hours, billableHours, notes, roleId, onSuccess) => {
										_handleWeekDayHoursInputChange(
											undefined,
											hours,
											undefined,
											undefined,
											undefined,
											lineItemNode,
											dayOfInput,
											dayRegs,
											selectedPerson,
											onSuccess
										);
									}}
									userpilot={'task-row-hours-input'}
									granularityFormatter={(val, intl, minuteLimit, showAsterisk) =>
										convertIntoFloatHoursFormatWithGranularity(
											val,
											intl,
											minuteLimit,
											showAsterisk,
											workingHourForTheDay(selectedPerson, dayOfInput)
										)
									}
									onlyFormatGranularityOnBlur
								/>
							);
						})}
					</div>
				);
			}
			case 'hour-inputs-day': {
				let notPermittedHourInputs = false;

				if (hasTopDownProgramBudgetFeature() && !isBillableSplitAllowed()) {
					const lineItemProject = lineItemNode.task ? lineItemNode.task.project : lineItemNode.project;
					notPermittedHourInputs =
						overBudgetProgramsByCompanyProjectId.find(
							project => project.companyProjectId === lineItemProject?.companyProjectId
						) &&
						(!task || task?.billable === true);
				}

				const lineItemRegistrationNotAllowedReason = getLineItemRegistrationNotAllowedReason(
					lineItemNode,
					selectedPerson.id
				);

				// Only show value for timeRegs or non-empty moved tasks

				const inputRegistrationNotAllowedReason = getInputRegistrationNotAllowedReason(
					currentViewingDate,
					// Day-view is a shit-show, lineItemNode can either be a time registration, task, or an idle time.
					// If it is a time registrationwe infer the entity from the not-null fields, otherwise we assume it is a task
					lineItemNode.task ? lineItemNode.task : lineItemNode.project ? lineItemNode.project : lineItemNode
				);
				const approvalStatus = usingTimeApproval && getTimeEntryApprovalStatus(lineItemNode, [], isWeekView);
				const isNonWorkingDay = holidays.length > 0 || workingHourForTheDay(selectedPerson, currentViewingDate) === 0;
				const infoText = getInfoTextForHoursInput(
					useTimeOffApproval,
					noAccess,
					lockedInternalTime,
					lockedTimeOff,
					inLockedPeriod,
					approvalStatus,
					invoiced,
					projectDone,
					projectHalted,
					false,
					false,
					hasAllocation,
					isWeekView,
					isInternalTimeDisabled,
					dayLockedInTime,
					periodTimeApprovalStatus,
					lineItemRegistrationNotAllowedReason,
					inputRegistrationNotAllowedReason,
					intl,
					notPermittedHourInputs,
					timeOffLockedByBambooHR,
					timeOffLockedByHiBob,
					manualProgress
				);
				const isDisabled =
					lineItemRegistrationNotAllowedReason ||
					inputRegistrationNotAllowedReason ||
					inLockedPeriod ||
					lockedInternalTime ||
					lockedTimeOff ||
					invoiced ||
					dayLockedInTime ||
					nothing ||
					noAccess ||
					projectHalted ||
					projectDone ||
					hasAllocation ||
					isInternalTimeDisabled ||
					notPermittedHourInputs ||
					timeOffLockedByBambooHR ||
					timeOffLockedByHiBob ||
					manualProgress;
				const key = currentViewingDate.format('x') + indexIndicator;
				const isBillable = lineItemNode.task?.billable || lineItemNode.billable || lineItemNode.project?.billable;
				const isBillableDifference =
					billableSplitAllowed && lineItemNode.minutesRegistered !== lineItemNode.billableMinutesRegistered;
				const hasTimeRegistered =
					!!lineItemNode.minutesRegistered || !!lineItemNode.billableMinutesRegistered || !!lineItemNode.notes;
				const taskEstimateForecastMinutes = lineItemNode.estimateForecastMinutes;
				const taskTotalMinutesRegistered = lineItemNode.totalMinutesRegistered;
				const remainingMinutes = getRemainingMinutes(lineItemNode);
				const validateRemaining = !isExceedingEstimateAllowed() && remainingMinutes !== undefined;
				const canHaveRole = !lineItemNode.isInternalTime;
				const isRoleDifferent = checkIfRoleIsDifferent(lineItemNode);
				const maxInputValue = validateRemaining
					? lineItemNode.minutesRegistered
						? remainingMinutes + lineItemNode.minutesRegistered
						: remainingMinutes
					: undefined;

				const hasDiff = (isBillable && isBillableDifference) || isRoleDifferent;

				return (
					<div
						id={`hour-inputs-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={
							'elem-wrapper day-view hours-input-wrapper' +
							(noteRequired ? ' overflow-visible' : '') +
							additionalClasses
						}
					>
						{usingAdvancedTimeRegistration || hasDiff ? (
							<ForecastTooltip content={infoText} maxWidth={250}>
								<HoursInputContainerStyle>
									<PopoverHandler>
										<PopoverHandler.Trigger asChild={true}>
											<HoursInputButton
												disabled={isDisabled}
												title={
													(isBillable && isBillableDifference) || isRoleDifferent
														? 'Time entry with additional properties'
														: null
												}
												nonWorkingDay={isNonWorkingDay}
												minutesRegistered={lineItemNode.minutesRegistered}
											>
												{((isBillable && isBillableDifference) || isRoleDifferent) && (
													<AdditionalPropertyDifferenceIndicator />
												)}
											</HoursInputButton>
										</PopoverHandler.Trigger>
										<PopoverHandler.Content showArrow={true}>
											{hasTimeRegistered ? (
												<UpdateAdvancedTimeRegistrationContent
													timeRegistrationsOnEntityOnDay={formatDayRegs([lineItemNode])}
													billable={isBillable}
													mutation={(hours, billableHours, notes, _, roleId, onSuccess) =>
														_handleDayHoursInputChange(
															hours,
															billableHours,
															roleId,
															notes,
															lineItemNode,
															selectedPerson,
															onSuccess
														)
													}
													selectedPerson={selectedPerson}
													dayOfInput={currentViewingDate}
													canHaveRole={canHaveRole}
													projectId={project?.id}
													taskId={lineItemNode.id}
												/>
											) : (
												<CreateAdvancedTimeRegistrationContent
													billable={isBillable}
													mutation={(hours, billableHours, notes, roleId, onSuccess) =>
														_handleDayHoursInputChange(
															hours,
															billableHours,
															roleId,
															notes,
															lineItemNode,
															selectedPerson,
															onSuccess
														)
													}
													isSuggestion={isSuggestion}
													selectedPerson={selectedPerson}
													dayOfInput={currentViewingDate}
													taskEstimateForecastMinutes={taskEstimateForecastMinutes}
													taskTotalMinutesRegistered={taskTotalMinutesRegistered}
													canHaveRole={canHaveRole}
													projectId={project?.id}
													taskId={lineItemNode.id}
												/>
											)}
										</PopoverHandler.Content>
									</PopoverHandler>
								</HoursInputContainerStyle>
							</ForecastTooltip>
						) : (
							<HoursInput
								key={key}
								infoText={infoText}
								disabled={isDisabled}
								nonWorkingDay={isNonWorkingDay}
								forceUpdate={forceUpdate}
								parentKeyDown={e => onInputKeyDown(e, rowNr * 7)}
								innerRef={div => (hourInputs[rowNr * 7] = div)}
								value={
									lineItemNode.minutesRegistered || isTimeReg
										? lineItemNode.minutesRegistered / 60
										: undefined
								}
								mutationValidChange={true}
								maxValueInMinutes={maxInputValue}
								mutation={(hours, billableHours, notes, roleId, onSuccess) =>
									_handleDayHoursInputChange(
										hours,
										undefined,
										undefined,
										undefined,
										lineItemNode,
										selectedPerson,
										onSuccess
									)
								}
								cy={'time-entry-hours-input'}
								userpilot={'task-row-hours-input'}
								granularityFormatter={(val, intl, minuteLimit, showAsterisk) =>
									convertIntoFloatHoursFormatWithGranularity(
										val,
										intl,
										minuteLimit,
										showAsterisk,
										workingHourForTheDay(selectedPerson, currentViewingDate)
									)
								}
								onlyFormatGranularityOnBlur
							/>
						)}
					</div>
				);
			}
			case 'notes-input': {
				const inputRegistrationNotAllowedReason = getInputRegistrationNotAllowedReason(
					currentViewingDate,
					lineItemNode.task ? lineItemNode.task : lineItemNode.project ? lineItemNode.project : lineItemNode
				);

				const isTimeRegDisabled =
					inputRegistrationNotAllowedReason ||
					inLockedPeriod ||
					lockedInternalTime ||
					lockedTimeOff ||
					invoiced ||
					dayLockedInTime ||
					nothing ||
					noAccess ||
					projectHalted ||
					projectDone ||
					hasAllocation ||
					isInternalTimeDisabled ||
					timeOffLockedByBambooHR ||
					timeOffLockedByHiBob ||
					manualProgress;

				return (
					<div
						id={`notes-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={
							'elem-wrapper notes-input-wrapper' +
							additionalClasses +
							(invoiced || inLockedPeriod ? ' disabled' : '')
						}
						data-cy={'notes-switch-task'}
					>
						<TextAreaFoldout
							value={isTimeReg ? lineItemNode.notes : lineItemNode.notes ? lineItemNode.notes : ''}
							timeRegId={isTimeReg && lineItemNode.id}
							onBlur={val => _handleNotesFieldBlur(lineItemNode, val, currentViewingDate.clone(), isTimeReg)}
							disabled={
								dayLockedInTime ||
								lockedInternalTime ||
								nothing ||
								noAccess ||
								invoiced ||
								inLockedPeriod ||
								isTimeRegDisabled
							}
							maxChars={viewer.company.characterLimit}
						/>
					</div>
				);
			}
			case 'notes-button':
				const inputRegistrationNotAllowedReason = getInputRegistrationNotAllowedReason(
					currentViewingDate,
					lineItemNode.task ? lineItemNode.task : lineItemNode.project ? lineItemNode.project : lineItemNode
				);

				const isTimeRegDisabled =
					inputRegistrationNotAllowedReason ||
					inLockedPeriod ||
					lockedInternalTime ||
					lockedTimeOff ||
					invoiced ||
					dayLockedInTime ||
					nothing ||
					noAccess ||
					projectHalted ||
					projectDone ||
					hasAllocation ||
					isInternalTimeDisabled ||
					timeOffLockedByBambooHR ||
					timeOffLockedByHiBob ||
					manualProgress;

				return (
					<div
						id={`notes-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper notes-button-wrapper' + additionalClasses}
					>
						<NotesPopOut
							locked={nothing}
							disabled={isTimeRegDisabled || (task && !task.fullAccessToProject)}
							characterLimit={viewer.company.characterLimit}
							timeRegItem={lineItemWithNotes(lineItemNode, lineItem.dayRegs)}
							taskName={task ? task.name : project ? project.name : idleTime ? idleTime.name : ''}
							lockedDate={lockedDate}
							usingInternalTimeApproval={usingInternalTimeApproval}
						/>
					</div>
				);
			case 'harvest':
				return getIsHarvestLineItem(lineItemNode) ? (
					<div key={lineItemNode.id + col.id} className={'elem-wrapper harvest' + additionalClasses}>
						<HarvestPopOut
							disabled={!viewer.harvestUser || _shouldDisableHarvestPopup(lineItemNode, isWeekView)}
							timeRegistrationsWithNotes={
								isWeekView
									? lineItemWithNotes(lineItemNode, lineItem.dayRegs).timeRegistrationsWithNotes
									: lineItemNode.minutesRegistered
									? [lineItemNode]
									: null
							}
							loadOnExpand={true}
							timeRegItem={lineItemNode}
							viewDate={currentViewingDate.clone()}
							selectedPerson={selectedPerson}
							taskName={
								lineItemNode.task
									? lineItemNode.task.name
									: lineItemNode.project
									? lineItemNode.project.name
									: lineItemNode.idleTime
									? lineItemNode.idleTime.name
									: lineItemNode.name
							}
							harvestTasks={harvestTasks}
							updateHarvestTasks={_fetchHarvestTasks.bind(
								this,
								lineItemNode.task
									? lineItemNode.task.project
									: lineItemNode.project
									? lineItemNode.project
									: lineItemNode
							)}
							integrationLoading={harvestLoading}
							lockedDate={lockedDate}
						/>
					</div>
				) : (
					<div key={lineItemNode.id + col.id} className={'elem-wrapper' + additionalClasses} />
				);
			case 'unit4':
				return getIsUnit4LineItem(lineItemNode) ? (
					<div key={lineItemNode.id + col.id} className={'elem-wrapper unit4' + additionalClasses}>
						<Unit4PopOut
							disabled={!selectedPerson.unit4User || _shouldDisableUnit4Popup(lineItemNode, isWeekView)}
							timeRegistrations={
								isWeekView
									? lineItemWithNotes(lineItemNode, lineItem.dayRegs).timeRegistrationsWithNotes
									: lineItemNode.minutesRegistered
									? [lineItemNode]
									: null
							}
							loadOnExpand={true}
							timeRegItem={lineItemNode}
							viewDate={currentViewingDate.clone()}
							selectedPerson={selectedPerson}
							taskName={
								lineItemNode.task
									? lineItemNode.task.name
									: lineItemNode.project
									? lineItemNode.project.name
									: lineItemNode.idleTime
									? lineItemNode.idleTime.name
									: lineItemNode.name
							}
							unit4Activities={unit4Activities}
							updateUnit4Activities={_fetchUnit4Activities.bind(
								this,
								lineItemNode.task
									? lineItemNode.task.project
									: lineItemNode.project
									? lineItemNode.project
									: lineItemNode
							)}
							integrationLoading={unit4Loading}
							lockedDate={lockedDate}
						/>
					</div>
				) : (
					<div key={lineItemNode.id + col.id} className={'elem-wrapper' + additionalClasses} />
				);
			case 'star':
				return (
					<div
						id={`star-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper star' + additionalClasses}
					>
						{((task && task.fullAccessToProject) || idleTime) && (
							<StarTask parentId={viewer.id} task={task || idleTime} isIdleTime={!!idleTime} />
						)}
					</div>
				);
			case 'edit':
				let editAction = _getActionMenuOptions(lineItemNode, isWeekView, lineItem.dayRegs).find(
					action => action.name === 'edit'
				);
				return (
					<div
						id={`edit-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper edit' + additionalClasses}
					>
						<TimeregActionButton
							disabled={
								!editAction || dayLockedInTime || !_isContextVisible(lineItemNode, lineItem.dayRegs, isWeekView)
							}
							text={formatMessage({id: 'time_registration.edit.header'})}
							icon="edit"
							onClick={() => {
								if (editAction) {
									editAction.onClick();
								}
							}}
							data-cy="timesheets-table-row-edit"
						/>
					</div>
				);
			case 'delete':
				let deleteAction = _getActionMenuOptions(lineItemNode, isWeekView, lineItem.dayRegs).find(
					action => action.name === 'delete'
				);
				let deleteLocked = isWeekView
					? lockedDate && lockedDate.isSameOrAfter(currentViewingDate.clone().startOf('week'), 'day')
					: dayLockedInTime;

				return (
					<div
						id={`delete-${indexIndicator}`}
						key={lineItemNode.id + col.id}
						className={'elem-wrapper delete' + additionalClasses}
					>
						<TimeregActionButton
							disabled={
								!deleteAction || deleteLocked || !_isContextVisible(lineItemNode, lineItem.dayRegs, isWeekView)
							}
							text={isWeekView ? formatMessage({id: 'common.delete_row'}) : formatMessage({id: 'common.delete'})}
							icon="trash"
							onClick={() => {
								if (deleteAction) {
									deleteAction.onClick();
								}
							}}
							data-cy="timesheets-table-row-delete"
						/>
					</div>
				);
			case 'switch-task-indicator':
				return (
					<div key={lineItemNode.id + col.id} className={'elem-wrapper switch-task-indicator' + additionalClasses}>
						{isSuggestion && showSuggestions ? (
							<ForecastTooltip
								spanStyle={{display: 'inline-flex'}}
								content={'Suggestion based on your historical timesheet entries'}
							>
								<Icon icon="sparkle" size="l" />
							</ForecastTooltip>
						) : null}
					</div>
				);
			default:
				if (col.id.startsWith(CUSTOM_FIELD_PREFIX)) {
					const columnCustomFieldDef = col.customFieldDefinition;
					const timeReg = isTimeReg && lineItemNode;
					const readOnly = !timeReg || columnCustomFieldDef.readOnly;
					const customField = timeReg?.customFields?.edges
						.map(edge => edge.node)
						.find(cfv => cfv.key === columnCustomFieldDef.key);
					const value = customField?.value;
					return (
						<div
							data-cy={'time-reg-table-cell-' + col.id}
							id={`${col.id}-${indexIndicator}`}
							key={lineItemNode.id + col.id}
							className={'elem-wrapper ' + (readOnly ? '' : 'custom-field-value-wrapper') + additionalClasses}
						>
							{readOnly ? (
								<span>{value ?? '-'}</span>
							) : (
								<CustomFieldValueInput
									customFieldKey={columnCustomFieldDef.key}
									displayName={columnCustomFieldDef.displayName}
									value={value}
									entityId={timeReg?.id}
									entityType={EntityType.TIME_REG}
									type={columnCustomFieldDef.type}
									options={columnCustomFieldDef.options}
								/>
							)}
						</div>
					);
				}
				return null;
		}
	};

	const getEmptyStateRow = columns => {
		const firstElem = columns[0];
		const lastElem = columns[columns.length - 1];

		return columns.map(col => {
			// for adding borders and padding to first and last element
			let additionalClasses = '';
			if (col === firstElem) {
				additionalClasses += ' first';
			}
			if (col === lastElem) {
				additionalClasses += ' last';
			}

			switch (col.id) {
				case 'task-name':
				case 'internal-time-name': {
					return (
						<EmptyStateTaskName
							searchActive={searchActive}
							filterActive={filterActive}
							additionalClasses={additionalClasses}
							usingCurrentUserAssigneeFilter={usingCurrentUserAssigneeFilter}
						/>
					);
				}
				default:
					return <div className={'elem-wrapper empty-state' + additionalClasses} />;
			}
		});
	};

	const getFooterColumn = (columns, timeAndWorkingHoursPerDay, isWeekView) => {
		const firstElem = columns[0];
		const lastElem = columns[columns.length - 1];

		return columns.map(col => {
			// for adding borders and padding to first and last element
			let additionalClasses = '';
			if (col === firstElem) {
				additionalClasses += ' first';
			}
			if (col === lastElem) {
				additionalClasses += ' last';
			}

			switch (col.id) {
				case 'task-name':
				case 'internal-time-name': {
					if (isWeekView) {
						const totals = timeAndWorkingHoursPerDay.reduce(
							(totals, entry) => {
								totals.time += entry.time;
								totals.hoursToWork += entry.hoursToWork;
								totals.billableTime += entry.billableNonBillableSplit.billableMinutes;
								return totals;
							},
							{time: 0, billableTime: 0, hoursToWork: 0}
						);
						return (
							<StickyFooterWrapper key={'totals-' + col.id}>
								<div
									className={
										'totals-elem-wrapper total-view hours-total-wrapper overflow-visible' +
										additionalClasses
									}
								>
									<TimesheetsTimeTableTotals>
										<TimesheetsTimeTableTotals.Combined
											minutesRegistered={totals.time}
											workPeriodInMinutes={totals.hoursToWork}
											billableMinutesRegistered={totals.billableTime}
											goToTimesheetsTab={goToTimesheetsTab}
										/>
									</TimesheetsTimeTableTotals>
								</div>
							</StickyFooterWrapper>
						);
					} else {
						return (
							<StickyFooterWrapper key={'totals-' + col.id}>
								<div
									className={
										'totals-elem-wrapper total-view hours-total-wrapper overflow-visible' +
										additionalClasses
									}
								>
									<TimesheetsTimeTableTotals align={ALIGN.END}>
										<TimesheetsTimeTableTotals.Labels
											goToTimesheetsTab={goToTimesheetsTab}
											align={ALIGN.END}
										/>
									</TimesheetsTimeTableTotals>
								</div>
							</StickyFooterWrapper>
						);
					}
				}
				case 'hour-inputs-week': {
					return (
						<StickyFooterWrapper key={'totals-' + col.id}>
							<div className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses}>
								{timeAndWorkingHoursPerDay.map((item, i) => {
									const dayTotals = totalsPerDay[i];
									if (dayTotals.hideDay) {
										return null;
									}
									return item.hoursToWork > 0 || item.noOfTimeRegistrations > 0 ? (
										isBillableSplitAllowed() ? (
											<TimeRegistrationDayTotals
												key={i}
												billableMinutesRegistered={item.billableNonBillableSplit.billableMinutes}
												minutesRegistered={item.time}
											/>
										) : (
											<CheckMarkAndTime
												key={i}
												time={item.time}
												hoursToWork={item.hoursToWork}
												showTime={!item.isHoliday}
												locked={lockedDate && lockedDate.isSameOrAfter(item.day, 'day')}
											/>
										)
									) : (
										<div key={i} />
									);
								})}
							</div>
						</StickyFooterWrapper>
					);
				}
				case 'hour-inputs-day': {
					const dayTotal = {
						time: timeAndWorkingHoursPerDay[0].time,
						billableTime: timeAndWorkingHoursPerDay[0].billableNonBillableSplit.billableMinutes,
						hoursToWork: timeAndWorkingHoursPerDay[0].hoursToWork,
					};
					return (
						<StickyFooterWrapper key={'totals-' + col.id}>
							<div className={'elem-wrapper total-view hours-total-wrapper-day' + additionalClasses}>
								<TimesheetsTimeTableTotals>
									<TimesheetsTimeTableTotals.Hours
										minutesRegistered={dayTotal.time}
										workPeriodInMinutes={dayTotal.hoursToWork}
										billableMinutesRegistered={dayTotal.billableTime}
										goToTimesheetsTab={goToTimesheetsTab}
										align={ALIGN.END}
									/>
								</TimesheetsTimeTableTotals>
							</div>
						</StickyFooterWrapper>
					);
				}
				case 'harvest': {
					return (
						<StickyFooterWrapper key={'totals-' + col.id}>
							<div className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses} />
						</StickyFooterWrapper>
					);
				}
				case 'unit4': {
					return (
						<StickyFooterWrapper key={'totals-' + col.id}>
							<div className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses} />
						</StickyFooterWrapper>
					);
				}

				// Not using these columns, so just throw a standard div
				case 'project-name':
				case 'project-indicator':
				case 'phase':
				case 'client':
				case 'remaining':
				case 'notes-input':
				case 'notes-button':
				case 'star':
				case 'edit':
				case 'delete': {
					return (
						<StickyFooterWrapper key={'totals-' + col.id}>
							<div className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses} />
						</StickyFooterWrapper>
					);
				}
				case 'switch-task-indicator': {
					return (
						<StickyFooterWrapper key={'totals-' + col.id}>
							<div
								className={
									'elem-wrapper total-view hours-total-wrapper switch-task-indicator' + additionalClasses
								}
							/>
						</StickyFooterWrapper>
					);
				}
				default:
					if (col.id.startsWith(CUSTOM_FIELD_PREFIX)) {
						return (
							<StickyFooterWrapper key={'totals-' + col.id}>
								<div className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses} />
							</StickyFooterWrapper>
						);
					}
					return null;
			}
		});
	};

	const getDayRows = entities => {
		const rows = entities.map((entity, index) => {
			const lastRow = index === entities.length - 1;
			return columns.map(col => getRowElemForCol(col, entity, index, lastRow));
		});
		if (thisWeekWork && rows.length === 0) {
			rows.push(getEmptyStateRow(columns));
		}
		const totals = getTimeAndWorkingHoursForPeriod(
			currentViewingDate,
			currentViewingDate,
			timeRegistrations,
			selectedPerson
		);
		rows.push(getFooterColumn(columns, totals));

		return rows;
	};

	const getWeekViewRows = entities => {
		const rows = entities.map((entity, index) => {
			const lastRow = index === entities.length - 1;
			return columns.map(col => getRowElemForCol(col, entity, index, lastRow));
		});
		if (thisWeekWork && rows.length === 0) {
			rows.push(getEmptyStateRow(columns));
		}
		rows.push(getFooterColumn(columns, totalsPerDay, isWeekView));
		return rows;
	};

	const rows = isWeekView ? getWeekViewRows(entityList) : getDayRows(entityList);

	// endregion

	// Only show EmptyState on Timesheet tab, not on ThisWeeksWork tab.
	// We always add the sticky footer row, so the empty rows check is length === 1
	const showEmptyState = !thisWeekWork && rows.length === 1;
	return (
		<TimeRowTableWrapper>
			{loading ? (
				<InlineLoader />
			) : showEmptyState ? (
				<EmptyState
					pageName={EMPTY_STATE.TIMESHEETS}
					filterOrSearchActive={filterActive || searchActive}
					showSuggestionsLink={hasSuggestions}
				/>
			) : (
				<div className={'grid-wrapper'} style={gridStyle}>
					{columns.map(col => getHeaderElemForCol(col))}
					{rows}
				</div>
			)}
		</TimeRowTableWrapper>
	);
};
